import React from 'react';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import { useSyncStore } from '../lib/offline/sync-store';
import { ConflictEntry } from '../lib/offline/db';
import { isPayLockRefusal } from '../lib/pay-lock';

function getEntityLabel(entry: ConflictEntry): string {
  const entity = entry.entity;
  const name = entry.serverRecord?.name
    || entry.serverRecord?.ticket_number
    || entry.serverRecord?.job_number
    || entry.serverRecord?.customer_name
    || entry.id.slice(0, 8);
  return `${entity.replace(/s$/, '')} ${name}`;
}

function getDiffFields(local: Record<string, any>, server: Record<string, any>): { key: string; local: any; server: any }[] {
  const diffs: { key: string; local: any; server: any }[] = [];
  for (const key of Object.keys(local)) {
    if (key.startsWith('_') || key === 'version' || key === 'updated_at') continue;
    const localVal = local[key];
    const serverVal = server[key];
    if (JSON.stringify(localVal) !== JSON.stringify(serverVal)) {
      diffs.push({ key, local: localVal, server: serverVal });
    }
  }
  return diffs;
}

interface Props {
  onClose: () => void;
}

const ConflictReviewPanel: React.FC<Props> = ({ onClose }) => {
  const { conflictDetails, dismissConflict, dismissAllConflicts } = useSyncStore();

  if (conflictDetails.length === 0) {
    return null;
  }
  // An approved-pay refusal or a paid-pay notice (api#1352). The server's
  // sentence is the whole explanation, so it is shown as the entry's text and
  // not as a muted aside. Neither shows the field diff. A notice has none,
  // because its edit DID save. A refused lock's diff is wrong: the queued
  // payload is camelCase and the server record snake_case, so every field read
  // "→ null" (measured on Smith, FT-00077), which says the server wiped the
  // ticket when it changed nothing. The sentence says what did not save.
  const isNotice = (e: ConflictEntry) => e.kind === 'warning';
  const isPayLock = (e: ConflictEntry) => isPayLockRefusal({ code: e.code });
  const anyConflict = conflictDetails.some(e => !isNotice(e));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-fieldiq-card border border-fieldiq-border rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-fieldiq-border sticky top-0 bg-fieldiq-card z-10">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-400" />
            <h2 className="text-base font-bold text-white">
              {anyConflict ? 'Sync Conflicts' : 'Sync Notices'} ({conflictDetails.length})
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {conflictDetails.length > 1 && (
              <button
                onClick={() => dismissAllConflicts()}
                className="text-xs text-fieldiq-accent hover:text-amber-300 font-medium"
              >
                Dismiss All
              </button>
            )}
            <button onClick={onClose} className="text-fieldiq-muted hover:text-white">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {anyConflict && (
            <p className="text-xs text-fieldiq-text">
              {conflictDetails.some(isNotice)
                ? 'Changes marked “Not saved” or with a conflict did not save. The server’s copy replaced those local edits.'
                : 'These changes did not save. The server’s copy replaced your local edits.'}
            </p>
          )}

          {conflictDetails.map((entry) => {
            const diffs = getDiffFields(entry.localData, entry.serverRecord);
            return (
              <div
                key={entry.clientMutationId}
                className="bg-fieldiq-dark rounded-lg border border-fieldiq-border p-3"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-white capitalize">
                      {getEntityLabel(entry)}
                    </p>
                    <p className="text-[10px] text-fieldiq-muted">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => dismissConflict(entry.clientMutationId)}
                    className="text-xs bg-fieldiq-card border border-fieldiq-border text-fieldiq-text hover:text-white px-2 py-1 rounded flex items-center gap-1"
                  >
                    <CheckCircle size={12} /> Dismiss
                  </button>
                </div>

                {entry.reason && (isNotice(entry) || isPayLock(entry)) ? (
                  <p className="text-xs text-fieldiq-text mb-2" data-testid="sync-entry-reason">
                    <span className="font-semibold">{isNotice(entry) ? 'Saved: ' : 'Not saved: '}</span>
                    {entry.reason}
                  </p>
                ) : entry.reason ? (
                  <p className="text-xs text-fieldiq-muted mb-2">{entry.reason}</p>
                ) : null}

                {isNotice(entry) || isPayLock(entry) ? null : diffs.length > 0 ? (
                  <div className="space-y-1">
                    {diffs.map((d) => (
                      <div key={d.key} className="text-xs">
                        <span className="text-fieldiq-muted">{d.key}:</span>{' '}
                        <span className="text-red-400 line-through">{String(d.local ?? 'null')}</span>
                        {' → '}
                        <span className="text-green-400">{String(d.server ?? 'null')}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-fieldiq-muted italic">No visible field differences</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConflictReviewPanel;
